import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { GroupForm } from '../GroupForm'
import { useRef, useEffect } from 'react'
import { FormValues as GroupFormValues } from '../GroupForm'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { UseFormReturn } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import useQueryGetMemberGroup from '@/modules/manage-member/api/hooks/useQueryGetMemberGroup'
import useQueryGetGroupById from '@/modules/manage-member/api/hooks/useQueryGetGroupById'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import { groupNameMapper } from '../../mappers'

const createFormSchema = z.object({
	username: z.string().min(1, 'กรุณากรอกชื่อผู้ใช้'),
	password: z.string().min(1, 'กรุณากรอกรหัสผ่าน'),
	phone: z.string().optional().nullable(),
	userGroupId: z
		.string({ required_error: 'กรุณาเลือกประเภทผู้ดูแลระบบ' })
		.min(1, 'กรุณาเลือกประเภทผู้ดูแลระบบ'),
})

const editFormSchema = z.object({
	username: z.string().min(1, 'กรุณากรอกชื่อผู้ใช้'),
	phone: z.string().optional().nullable(),
	userGroupId: z
		.string({ required_error: 'กรุณาเลือกประเภทผู้ดูแลระบบ' })
		.min(1, 'กรุณาเลือกประเภทผู้ดูแลระบบ'),
})

export type FormValues = z.infer<typeof createFormSchema>

interface Props {
	defaultValues?: Partial<FormValues>
	onSubmit: (values: FormValues, form: UseFormReturn<FormValues>) => void
	mode: 'create' | 'edit'
}

export function MemberForm({ defaultValues, onSubmit, mode }: Props) {
	const navigate = useNavigate()
	const groupFormRef = useRef<UseFormReturn<GroupFormValues>>(null)
	const payload = {
		skip: 0,
		take: 1000,
	}
	const { data: groups } = useQueryGetMemberGroup(payload)

	const form = useForm<FormValues>({
		resolver: zodResolver(
			mode === 'create' ? createFormSchema : editFormSchema,
		),
		defaultValues: {
			username: defaultValues?.username || '',
			password: defaultValues?.password || '',
			phone: defaultValues?.phone,
			userGroupId: defaultValues?.userGroupId,
		},
	})

	const selectedGroupId = form.watch('userGroupId')
	const { data: selectedGroup } = useQueryGetGroupById(selectedGroupId, {
		enabled: !!selectedGroupId,
		queryKey: ['group', selectedGroupId],
	})

	useEffect(() => {
		if (selectedGroup && groupFormRef.current) {
			groupFormRef.current.setValue(
				'writePermission',
				selectedGroup.writePermission as any,
			)
			groupFormRef.current.setValue(
				'readPermission',
				selectedGroup.readPermission as any,
			)
			groupFormRef.current.setValue('name', selectedGroup.name)
		}
	}, [selectedGroup])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const isValid = await form.trigger()
		if (!isValid) return
		const values = form.getValues()
		onSubmit(values, form)
	}

	return (
		<Form {...form}>
			<form onSubmit={handleSubmit} className="space-y-8">
				<div className="grid grid-cols-2 gap-8">
					<div className="space-y-8">
						<FormField
							control={form.control}
							name="username"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										<RequiredIndicator /> ชื่อผู้ดูแลระบบ
									</FormLabel>
									<FormControl>
										<Input placeholder="กรอกชื่อผู้ใช้" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{mode === 'create' && (
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> รหัสผ่าน
										</FormLabel>
										<FormControl>
											<Input
												type="password"
												placeholder="กรอกรหัสผ่าน"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						<FormField
							control={form.control}
							name="phone"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เบอร์โทรศัพท์</FormLabel>
									<FormControl>
										<Input
											placeholder="กรอกเบอร์โทรศัพท์"
											type="number"
											onKeyDown={(e) => {
												if (['e', 'E', '+', '-'].includes(e.key)) {
													e.preventDefault()
												}
											}}
											{...field}
											value={field.value || ''}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="userGroupId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										<RequiredIndicator /> ประเภทผู้ดูแลระบบ
									</FormLabel>
									<Select
										onValueChange={field.onChange}
										defaultValue={field.value}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกประเภทผู้ดูแลระบบ" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{groups?.data.map((group) => (
												<SelectItem key={group.id} value={group.id}>
													{groupNameMapper[group.name] || group.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						<div className="flex gap-4">
							<Button type="submit" variant="default">
								{mode === 'create' ? 'สร้างผู้ใช้งาน' : 'แก้ไขผู้ใช้งาน'}
							</Button>
							<Button
								type="button"
								variant="outline"
								onClick={() => navigate(-1)}
							>
								ยกเลิก
							</Button>
						</div>
					</div>

					<div>
						<GroupForm
							formRef={groupFormRef}
							defaultValues={
								(selectedGroup as Partial<GroupFormValues>) || {
									name: '',
									writePermission: [],
									readPermission: [],
								}
							}
							onSubmit={() => {}}
							submitText=""
							isDisabled={true}
							hideButtons={true}
							hideNameField={true}
						/>
					</div>
				</div>
			</form>
		</Form>
	)
}
